import React from 'react';
import { Link } from 'react-router-dom';
import Spinner from '../spinner';
import { LeaderboardListItem } from '../LeaderboardListItem';
import type { LeaderboardItemConfig } from '../LeaderboardListItem';
import { getDonorName } from '../../../utils/string/nameUtils';
import type { Donation } from '../../../types/leaderboard';
import styles from './DonationList.module.css';

interface DonationListProps {
  donations: Donation[];
  maxItems?: number;
  viewAllLink?: string;
  viewAllText?: string;
  formatTimeElapsed: (date: string) => string;
  emptyStateMessage?: string;
  showRank?: boolean;
  onDonationClick?: (donation: Donation) => void;
  isLoading?: boolean;
}

export const DonationList: React.FC<DonationListProps> = ({
  donations,
  maxItems = 10,
  viewAllLink,
  viewAllText = 'View all',
  formatTimeElapsed,
  emptyStateMessage = 'No donations yet',
  showRank = false,
  onDonationClick,
  isLoading = false,
}) => {
  const displayedDonations = (donations || []).slice(0, maxItems);

  // Configuration for donation items
  const donationConfig: LeaderboardItemConfig<Donation> = {
    getId: (d) => d.id,
    getName: (d) => getDonorName(d),
    getAmount: (d) => d.amount || 0,
    getMetadata: (d) => {
      const dateTime = d.donatedAtLocal || d.createdAtLocal;
      if (!dateTime) return '';
      const formatted = formatTimeElapsed(dateTime);
      return formatted === '—' ? '' : formatted;
    },
    getLink: (d) => `/donations/view/${d.id}`,
    getCurrency: (d) => d.currency,
    getImageUrl: () => null, // Donations don't have images, will show initials
    showAvatar: true,
    fullRowClickable: true,
  };

  return (
    <div className={styles.leaderboardList}>
      {isLoading ? (
        <div className={styles.emptyState}>
          <Spinner />
        </div>
      ) : !donations || donations.length === 0 ? (
        <div className={styles.emptyState}>
          <p>{emptyStateMessage}</p>
        </div>
      ) : (
        <>
          {displayedDonations.map((donation, index) => (
            <LeaderboardListItem
              key={donation.id || index}
              item={donation}
              rank={index + 1}
              showRank={showRank}
              config={donationConfig}
              onClick={onDonationClick}
            />
          ))}
          {viewAllLink && (
            <Link to={viewAllLink} className={styles.viewAllButton}>
              {viewAllText}
            </Link>
          )}
        </>
      )}
    </div>
  );
};
